import { ArchiveIcon } from '@radix-ui/react-icons';
import { Button, Callout, Flex, Text } from '@radix-ui/themes';
import React, { ReactNode } from 'react';

interface EmptyStateProps {
    title?: string;
    description?: string;
    icon?: ReactNode;
    actionLabel?: string;
    onAction?: () => void;
}

/**
 * Estado vazio partilhado pelas listagens. Substitui a tabela em branco por
 * uma mensagem explícita e, quando aplicável, o atalho para criar o registo.
 */
export const EmptyState = ({
    title = 'Sem registos',
    description = 'Ainda não existem dados para apresentar.',
    icon,
    actionLabel,
    onAction,
}: EmptyStateProps) => (
    <Flex
        direction="column"
        align="center"
        justify="center"
        gap="3"
        py="8"
        px="4"
        style={{
            border: '1px dashed var(--gray-6)',
            borderRadius: 'var(--radius-3)',
            backgroundColor: 'var(--gray-2)',
        }}
    >
        <Flex
            align="center"
            justify="center"
            style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-3)',
                backgroundColor: 'var(--gray-3)',
                color: 'var(--gray-9)',
            }}
        >
            {icon ?? <ArchiveIcon width="20" height="20" />}
        </Flex>

        <Flex direction="column" align="center" gap="1">
            <Text size="3" weight="medium">{title}</Text>
            <Text size="2" color="gray" align="center">{description}</Text>
        </Flex>

        {actionLabel && onAction && (
            <Button variant="soft" onClick={onAction}>{actionLabel}</Button>
        )}
    </Flex>
);

interface ErrorStateProps {
    title?: string;
    description?: string;
    onRetry?: () => void;
}

/** Estado de erro partilhado pelas listagens. */
export const ErrorState = ({
    title = 'Erro ao carregar os dados',
    description = 'Não foi possível obter a informação. Por favor, tente novamente.',
    onRetry,
}: ErrorStateProps) => (
    <Callout.Root color="red" role="alert">
        <Callout.Icon>
            <ArchiveIcon />
        </Callout.Icon>
        <Callout.Text>
            <Text as="span" weight="medium">{title}</Text> — {description}
        </Callout.Text>
        {onRetry && (
            <Flex justify="end" mt="2">
                <Button size="1" variant="soft" color="red" onClick={onRetry}>
                    Tentar novamente
                </Button>
            </Flex>
        )}
    </Callout.Root>
);

export default EmptyState;
